import React, {Component} from 'react';
import {DatePicker, DefaultButton, Dropdown, DropdownMenuItemType, IDropdownOption} from "office-ui-fabric-react";
import DispatchProps from "../../../../utils/DispatchProps";
import {WorkloadInstanceQuery} from "../../../../models/WorkloadInstanceQuery";
import {DurableFunctionInstanceStatus} from "../../../../models/DurableFunctionInstance";
import {friendlySchoolName} from "../../../../utils/Utils";
import moment from "moment";
import {IndexedSchools} from "../../../../utils/IndexedSchools";
import * as _ from "lodash";
import ApiService from "../../../ApiService";

interface WorkloadQueryControlsProps {
    workloadAndPlaylistOptions: IDropdownOption[];
    indexedSchools?: IndexedSchools;
    
    schoolOptionDisabled: boolean;

    workloadInstanceQuery: WorkloadInstanceQuery;
    onQueryChanged (query: WorkloadInstanceQuery): void;
    onResetQueryClicked(): void;
}

interface WorkloadQueryControlsState {
    workloadAndPlaylistOptionsWithAll: IDropdownOption[];
    schoolOptions: IDropdownOption[];
}

export default class WorkloadQueryControls extends Component<WorkloadQueryControlsProps,WorkloadQueryControlsState> {
    
    static async fetchWorkloadAndPlaylistOptions(apiService: ApiService): Promise<IDropdownOption[]>
    {
        const workloadInfos = await apiService.getWorkloads();
        const playlists = await apiService.getPlaylists();

        const workloadOptions = _.sortBy(workloadInfos, ["name"]).map((workload): IDropdownOption => {
            return {
                key: workload.name,
                text: workload.name,
                data: {
                    name: workload.name,
                    isPlaylist: false,
                    visibleToAdminsOnly: workload.visibleToAdminsOnly
                }
            };
        });

        const playlistOptions = _.sortBy(playlists, ["name"]).map((playlist): IDropdownOption => {
            return {
                key: playlist.name,
                text: playlist.name,
                data: {
                    name: playlist.name,
                    isPlaylist: true
                }
            };
        })

        const workloadHeader = { key: 'workloadHeader', text: 'Workloads', itemType: DropdownMenuItemType.Header };
        const divider = { key: 'divider', text: '-', itemType: DropdownMenuItemType.Divider };
        const playlistHeader = { key: 'playlistHeader', text: 'Playlists', itemType: DropdownMenuItemType.Header };

        return [workloadHeader, ...workloadOptions, divider, playlistHeader, ...playlistOptions]
    }
    
    static displayName = WorkloadQueryControls.name;

    constructor(props: WorkloadQueryControlsProps) {
        super(props);
        
        this.state = {
            workloadAndPlaylistOptionsWithAll: [],
            schoolOptions: [],
        }
    }


    async componentDidMount(): Promise<void> {
        
        this.updateOptions();
        
        if(this.props.indexedSchools)
        {
            this.updateSchoolOptions();
        }
    }

    async componentDidUpdate(prevProps: Readonly<WorkloadQueryControlsProps & DispatchProps>, prevState: WorkloadQueryControlsState): Promise<void> {
        
        if(this.props.workloadAndPlaylistOptions !== prevProps.workloadAndPlaylistOptions)
        {
            this.updateOptions();
        }
        
        if(this.props.indexedSchools !== prevProps.indexedSchools)
        {
            this.updateSchoolOptions();
        }
    }

    private updateOptions() {
        if(this.props.workloadAndPlaylistOptions)
        {
            let optionsWithAll = [...this.props.workloadAndPlaylistOptions];

            optionsWithAll.splice(0,0,{
                key: "All",
                text: "All",
                data: undefined
            });

            this.setState({
                workloadAndPlaylistOptionsWithAll: optionsWithAll,
            });
        }
    }
    
    private updateSchoolOptions() {
        
        let schoolOptions = Object.values(this.props.indexedSchools!.tenantIdSchoolCode).map((school): IDropdownOption => {
            return {
                key: `${school.tenantId}-${school.schoolCode}`,
                text: friendlySchoolName(school),
                data: school,
            }
        });
        
        schoolOptions = _.sortBy(schoolOptions,o => o.text.toLowerCase());

        schoolOptions.splice(0,0,{
            key: "All",
            text: "All",
            data: {tenantId: undefined, schoolCode: undefined},
        });

        this.setState({
            schoolOptions: schoolOptions
        });
    }
    
    private _onNameOptionSelected = (event: any,option?: IDropdownOption) => {
        if(!option)
        {
            return;
        }
        
        if(option.key === "All")
        {
            delete this.props.workloadInstanceQuery.workloadName;
            delete this.props.workloadInstanceQuery.playlistName;
        }
        else if(option.data.isPlaylist)
        {
            this.props.workloadInstanceQuery.playlistName = option.data.name;
            delete this.props.workloadInstanceQuery.workloadName;
        }
        else
        {
            this.props.workloadInstanceQuery.workloadName = option.data.name;
            delete this.props.workloadInstanceQuery.playlistName;
        }

        this.props.onQueryChanged(WorkloadInstanceQuery.copyOf(this.props.workloadInstanceQuery));
    };

    private _onSchoolOptionSelected = (event: any,option?: IDropdownOption) => {
        if(!option)
        {
            return;
        }

        this.props.workloadInstanceQuery.tenantId = option.data.tenantId?.toLowerCase();
        this.props.workloadInstanceQuery.schoolCode = option.data.schoolCode?.toLowerCase();

        this.props.onQueryChanged(WorkloadInstanceQuery.copyOf(this.props.workloadInstanceQuery));
    };

    private _onSelectFromDate = (date: Date | undefined | null) => {
        this.props.workloadInstanceQuery.fromDate = date!;

        this.props.onQueryChanged(WorkloadInstanceQuery.copyOf(this.props.workloadInstanceQuery));
    };
    
    private _onSelectToDate = (date: Date | undefined | null) => {
        this.props.workloadInstanceQuery.toDate = date!;

        this.props.onQueryChanged(WorkloadInstanceQuery.copyOf(this.props.workloadInstanceQuery));

    };
    
    
    private buildSchoolDropdown(): JSX.Element {
        return (
            <Dropdown
                className="filter-controls__input"
                label="School"
                selectedKey={this.props.workloadInstanceQuery.tenantId ? `${this.props.workloadInstanceQuery.tenantId}-${this.props.workloadInstanceQuery.schoolCode}`: "All"}
                onChange={this._onSchoolOptionSelected}
                placeholder={"All"}


                options={this.state.schoolOptions}
                styles={{ dropdown: { width: 300 } }}
            />
        );
    }
    
    private buildStatusTogglePill(status: DurableFunctionInstanceStatus, enabledColour: string): JSX.Element {
        
        const indexOfStatusInQuery = this.props.workloadInstanceQuery.status.indexOf(status);
        const statusEnabled = indexOfStatusInQuery !== -1;
        
        
        const onPillClick = () => {

            if(statusEnabled)
            {
                this.props.workloadInstanceQuery.status.splice(indexOfStatusInQuery,1);
            }
            else
            {
                this.props.workloadInstanceQuery.status.push(status);
            }
            
            this.props.onQueryChanged(WorkloadInstanceQuery.copyOf(this.props.workloadInstanceQuery));
        };
        
        return (<span className={`filter-controls__input pill pill--${statusEnabled ? enabledColour : "grey"}`} onClick={onPillClick}>{status}</span>)
    }
    
    private timeChanged(timeSelected: string, date: Date,onSelectDate: (dateTime: Date) => void) {

        const timeParts: any[] = timeSelected.split(":");

        const newDate = moment(date);
        newDate.set("hours",timeParts[0]);
        newDate.set("minutes",timeParts[1]);
        
        onSelectDate(newDate.toDate())
    }
    
    render(): JSX.Element
    {
        return (
            <div className="filter-controls">
                
                <div className="filter-controls__row">
                    {this.props.schoolOptionDisabled ? null : this.buildSchoolDropdown()}
                    <Dropdown
                        className="filter-controls__input"
                        label="Workload/Playlist"
                        selectedKey={this.props.workloadInstanceQuery.workloadName || this.props.workloadInstanceQuery.playlistName || "All"}
                        onChange={this._onNameOptionSelected}
                        placeholder={"All"}
                        options={this.state.workloadAndPlaylistOptionsWithAll}
                        styles={{ dropdown: { width: 300 } }}
                    />
                    <DatePicker
                        className={"filter-controls__input"}
                        label={"From"}
                        value={this.props.workloadInstanceQuery.fromDate}
                        onSelectDate={this._onSelectFromDate}
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                    />
                    
                    <div className={"filter-controls__input filter-controls__input--time"}>
                        <label>At (UTC)</label>
                        <input type={"time"} value={moment(this.props.workloadInstanceQuery.fromDate).format("HH:mm")}
                               onChange={(e: any) => {
                                   this.timeChanged(e.target.value,this.props.workloadInstanceQuery.fromDate!,this._onSelectFromDate);
                               }}/>
                    </div>

                    <DatePicker
                        className={"filter-controls__input"}
                        label={"To"}
                        value={this.props.workloadInstanceQuery.toDate}
                        onSelectDate={this._onSelectToDate}
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                    />

                    <div className={"filter-controls__input filter-controls__input--time"}>
                        <label>At (UTC)</label>
                        <input type={"time"} value={moment(this.props.workloadInstanceQuery.toDate).format("HH:mm")}
                               onChange={(e: any) => {
                                   this.timeChanged(e.target.value,this.props.workloadInstanceQuery.toDate!,this._onSelectToDate);
                               }}/>
                    </div>
                    
                </div>
                
                <div className="filter-controls__row">
                    {this.buildStatusTogglePill("Completed","green")}
                    {this.buildStatusTogglePill("Running","amber")}
                    {this.buildStatusTogglePill("Failed","red")}
                    {this.buildStatusTogglePill("Terminated","black")}
                    {this.buildStatusTogglePill("Pending","blue")}
                    
                    <DefaultButton className={"filter-controls__input m-left-auto"} text="Reset" onClick={this.props.onResetQueryClicked} allowDisabledFocus/>

                </div>
                
            </div>
 
        );
    }
}